import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import "./css/LoginFormModal.css";
import UserAPI from "../services/UserAPI"; // Import the UserAPI service
import UserAuthAPI from "../services/UserAuthAPI"; // Import the UserAuthAPI service
import { setInMemoryToken } from "../services/APIService"; // Secure session-scoped token setter
import { notifyAuthChanged } from "../services/authEvents";
import BrowserFingerprintAPI from "../services/BrowserFingerprintAPI"; // Import the BrowserFingerprintAPI service
import MfaAPI from "../services/MfaAPI"; // Import the MfaAPI service
import SubscriptionsAPI from "../services/SubscriptionsAPI"; // Import the SubscriptionsAPI service
import { useUser } from "../context/UserContext"; // Import UserContext
import { useTheme } from "../context/ThemeContext"; // Import ThemeContext
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faArrowLeft,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import MfaVerificationModal from "./MfaVerificationModal";
import TermsAndConditions from "../../legal/TermsAndConditions";
import PrivacyPolicy from "../../legal/PrivacyPolicy";

function LoginFormModal({
  onClose,
  onCreateAccount,
  onForgotPassword,
  setIsPhoneVerificationPopupOpen,
  setHeroContent,
  setSmsSuccessfullySent,
}) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
  const [isMouseDownOnBackdrop, setIsMouseDownOnBackdrop] = useState(false); // Track mousedown on backdrop
  const [currentView, setCurrentView] = useState("login"); // "login", "terms", or "privacy"

  // MFA state
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaSessionId, setMfaSessionId] = useState(null);
  const [tempLoginData, setTempLoginData] = useState(null);

  // Get loginUser and selectUser functions from UserContext
  const { loginUser, selectUser } = useUser();

  // Get current theme
  const { effectiveTheme } = useTheme();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setErrorMessage("Both fields are required.");
      return;
    }

    try {
      console.log(
        "LoginFormModal: Starting parallel authentication and fingerprint collection",
      );

      // Set authentication flags BEFORE making any API calls
      console.log(
        "🚀 LoginFormModal: Setting authenticationStarting=true BEFORE login API call",
      );
      window.authenticationStarting = true;
      window.authenticationPromise = new Promise((resolve) => {
        window.resolveAuthentication = resolve;
      });

      // Create authentication function for parallel execution
      const authFunction = async (credentials) => {
        return await UserAuthAPI.login(credentials);
      };

      // Execute authentication and fingerprint collection in parallel
      // Authentication will complete independently of fingerprinting
      const data = await BrowserFingerprintAPI.parallelAuthAndFingerprint(
        authFunction,
        formData,
      );

      console.log(
        "LoginFormModal: Authentication completed successfully:",
        data,
      );

      // Resolve authentication promise immediately after successful login
      console.log("🏁 LoginFormModal: Resolving authentication promise");
      if (window.resolveAuthentication) {
        window.resolveAuthentication(true);
        window.resolveAuthentication = null;
      }

      // Check if MFA is required
      if (data.requiresMfa) {
        console.log("LoginFormModal: MFA verification required");
        setMfaSessionId(data.mfaSessionId);
        setTempLoginData(data);
        setShowMfaModal(true);
        setErrorMessage("");
        // Don't clear auth flags yet - MFA is part of the login flow
        return;
      }

      // Continue with normal login flow
      await completeLogin(data);
    } catch (error) {
      console.error("LoginFormModal: Error during login:", error);

      // Differentiate between different types of errors
      let errorMessage = "Invalid email or password."; // Default for authentication errors

      // Check for email verification error first (most specific)
      if (
        error.status === 403 &&
        error.message &&
        error.message.includes("not verified")
      ) {
        errorMessage = error.message; // Use the exact message from backend
      } else if (
        error.status === 403 &&
        error.message &&
        error.message.startsWith("This demo account")
      ) {
        errorMessage = error.message; // Show demo seat status message as-is
      } else if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        // Network/CORS/Server errors
        errorMessage = "Server error. Please try again later.";
      } else if (error.message && error.message.includes("CORS")) {
        // CORS-specific errors
        errorMessage = "Connection error. Please try again later.";
      } else if (error.message && error.message.includes("Network")) {
        // Network errors
        errorMessage = "Network error. Please check your connection.";
      } else if (error.name === "AbortError") {
        // Request timeout
        errorMessage = "Request timeout. Please try again.";
      } else if (
        error.message &&
        (error.message.includes("500") ||
          error.message.includes("Internal Server Error"))
      ) {
        // Server errors
        errorMessage = "Server error. Please try again later.";
      } else if (
        error.message &&
        error.message.includes("User account not found")
      ) {
        // User not found error
        errorMessage = error.message;
      } else if (
        error.message &&
        error.message.includes("Invalid credentials")
      ) {
        // Invalid credentials error
        errorMessage = "Invalid email or password.";
      }
      // For other 401/403 or specific authentication errors, keep the default message

      setErrorMessage(errorMessage);

      // Clean up authentication flags on error
      console.log(
        "❌ LoginFormModal: Login failed, cleaning up authentication flags",
      );
      window.authenticationStarting = false;
      if (window.resolveAuthentication) {
        window.resolveAuthentication(false);
        window.resolveAuthentication = null;
      }
      window.authenticationPromise = null;
    }
  };

  const handleMfaVerified = async (verificationData) => {
    try {
      console.log(
        "LoginFormModal: MFA verification successful, received data:",
        verificationData,
      );

      // MFA verification successful, complete login
      const loginData = { ...tempLoginData, ...verificationData };
      console.log("LoginFormModal: Combined login data after MFA:", loginData);

      // Resolve authentication promise for MFA flow
      console.log(
        "🏁 LoginFormModal: Resolving authentication promise (MFA complete)",
      );
      if (window.resolveAuthentication) {
        window.resolveAuthentication(true);
        window.resolveAuthentication = null;
      }

      setShowMfaModal(false);
      setMfaSessionId(null);
      setTempLoginData(null);
      await completeLogin(loginData);
    } catch (error) {
      console.error("LoginFormModal: Error completing login after MFA:", error);
      setErrorMessage("Login failed. Please try again.");
      setShowMfaModal(false);

      // Clean up authentication flags on MFA error
      console.log(
        "❌ LoginFormModal: MFA error, cleaning up authentication flags",
      );
      window.authenticationStarting = false;
      if (window.resolveAuthentication) {
        window.resolveAuthentication(false);
        window.resolveAuthentication = null;
      }
      window.authenticationPromise = null;
    }
  };

  const handleMfaCancel = () => {
    setShowMfaModal(false);
    setMfaSessionId(null);
    setTempLoginData(null);
    setErrorMessage("Login cancelled");

    // Clean up authentication flags on MFA cancel
    console.log(
      "❌ LoginFormModal: MFA cancelled, cleaning up authentication flags",
    );
    window.authenticationStarting = false;
    if (window.resolveAuthentication) {
      window.resolveAuthentication(false);
      window.resolveAuthentication = null;
    }
    window.authenticationPromise = null;
  };

  const handleResendMfaCode = async () => {
    try {
      await MfaAPI.resendMfaCode(mfaSessionId);
    } catch (error) {
      console.error("LoginFormModal: Error resending MFA code:", error);
      throw error;
    }
  };

  // Subscription statuses that grant feature access. Mirrors the
  // ALWAYS_ACTIVE_STATUSES set in SubscriptionContext so the post-login
  // landing-page decision uses the same definition of "active subscription"
  // as the rest of the app.
  const ACTIVE_SUBSCRIPTION_STATUSES = new Set([
    "ACTIVE",
    "PAST_DUE",
    "GRACE_PERIOD",
  ]);

  const isSubscriptionActive = (sub) => {
    if (!sub || !sub.status) return false;
    if (ACTIVE_SUBSCRIPTION_STATUSES.has(sub.status)) return true;
    if (sub.status === "CANCELLED") {
      const endDateRaw = sub.endDate || sub.accessEndDate;
      if (!endDateRaw) return false;
      const endDate = new Date(endDateRaw);
      if (Number.isNaN(endDate.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return endDate >= today;
    }
    return false;
  };

  /**
   * Decide which page to land on after a successful login.
   *
   * Default behavior is to honor the user's saved `landingPage` preference
   * (Dashboard, Getting Started, How do I...?). However, if the user does
   * not have an active subscription, force them to the Dashboard so they
   * see the Account Setup Progress (expanded) and the prominent
   * "choose a plan" prompt rendered there.
   */
  const resolveLandingPageAfterLogin = async () => {
    const preferredLandingPage =
      localStorage.getItem("landingPage") || "Dashboard";

    try {
      const response = await SubscriptionsAPI.getCurrentSubscription();
      const sub = response?.success ? response?.data : null;

      if (!isSubscriptionActive(sub)) {
        console.log(
          "LoginFormModal: No active subscription detected — routing to Dashboard with Account Setup Progress expanded",
        );
        // Signal AccountSetupJourney to render in expanded state.
        sessionStorage.setItem("expandSetupJourney", "true");
        return "Dashboard";
      }
    } catch (err) {
      console.warn(
        "LoginFormModal: Could not verify subscription status after login; falling back to user preference",
        err,
      );
    }

    return preferredLandingPage;
  };

  const completeLogin = async (data) => {
    // Clear authentication promise here since we're done with initial auth
    console.log("🏁 LoginFormModal: Starting completeLogin");

    // SECURITY: JWT token is set as HttpOnly cookie by server (not accessible to JavaScript)
    // This protects against XSS attacks - token cannot be stolen via document.cookie or localStorage
    // Both API requests AND WebSocket authentication use the HttpOnly cookie automatically
    // Browser sends cookie in all requests including WebSocket handshake
    console.log(
      "LoginFormModal: Authentication via HttpOnly cookie (token not exposed to JavaScript)",
    );

    // Note: sessionId is now set as HttpOnly cookie by the server
    // No need to manually set it in frontend anymore
    console.log(
      "LoginFormModal: Session ID is automatically set as HttpOnly cookie by server",
    );

    // Store critical auth data FIRST before clearing auth flags.
    // This prevents a race window where useRoleCheck sees auth flags cleared but
    // jwtExpiry not yet written, causing isTokenExpired() to return true and
    // firing a premature logoutUser() call.
    if (data.roles && Array.isArray(data.roles)) {
      localStorage.setItem("roles", JSON.stringify(data.roles));
      console.log(
        "LoginFormModal: User roles stored in localStorage:",
        data.roles,
      );
    } else {
      console.warn("LoginFormModal: No roles found in login response");
    }

    // Cross-browser auth fallback: store the JWT in the secure in-memory /
    // sessionStorage token store so APIService can attach it as an
    // Authorization: Bearer header. This guarantees that authenticated calls
    // succeed even on browser/OS combinations that drop the cross-origin
    // HttpOnly authToken cookie (observed on some Chrome/macOS configurations).
    // sessionStorage is tab-isolated and auto-cleared on tab close, so the
    // token does not persist across browser sessions.
    if (data.token) {
      setInMemoryToken(data.token);
      console.log(
        "LoginFormModal: JWT cached in session-scoped store for Bearer-header auth",
      );
    } else {
      console.warn(
        "LoginFormModal: No JWT token in login response — Bearer-header fallback unavailable",
      );
    }

    // Store JWT expiry in localStorage immediately after roles
    if (data.jwtExpiry) {
      localStorage.setItem("jwtExpiry", data.jwtExpiry.toString());
      console.log(
        "LoginFormModal: JWT expiry stored in localStorage:",
        new Date(data.jwtExpiry),
      );
    } else {
      console.warn("LoginFormModal: No JWT expiry found in login response");
    }

    notifyAuthChanged();

    // NOW it is safe to clear auth flags — localStorage is already populated so
    // any concurrent isTokenExpired() call will find a valid jwtExpiry.
    console.log(
      "🏁 LoginFormModal: Clearing authentication promise after localStorage writes",
    );
    window.authenticationPromise = null;
    window.authenticationStarting = false;

    // SECURITY: groupId is NO LONGER stored in localStorage or passed from backend
    // Group information is now derived from the authenticated user's UUID on the backend
    // Only fetch group user count if user has required roles (group_member, group_admin, account_admin, platform_admin)
    const allowedRoles = [
      "group_member",
      "group_admin",
      "account_admin",
      "platform_admin",
    ];
    const userRoles = data.roles || [];
    const hasGroupAccess = userRoles.some((role) =>
      allowedRoles.includes(role),
    );

    if (hasGroupAccess) {
      try {
        const countResponse = await UserAPI.getUserCountInMyGroup();
        if (countResponse && countResponse.count !== undefined) {
          localStorage.setItem("groupUserCount", countResponse.count);
        }
      } catch (err) {
        console.error("LoginFormModal: Failed to fetch group user count:", err);
      }
    } else {
      console.log(
        "LoginFormModal: User does not have group access roles, skipping group count fetch",
      );
    }

    // Sync user settings from login response into localStorage cache
    if (data.userSettings) {
      console.log(
        "LoginFormModal: Syncing user settings from login response:",
        data.userSettings,
      );
      const s = data.userSettings;
      // Dashboard & UI
      if (s.dashboardView != null)
        localStorage.setItem("dashboardView", s.dashboardView);
      if (s.dashboardMode != null)
        localStorage.setItem("dashboardMode", s.dashboardMode);
      if (s.showViewSettings != null)
        localStorage.setItem("showViewSettings", s.showViewSettings.toString());
      if (s.defaultLandingPage != null)
        localStorage.setItem("landingPage", s.defaultLandingPage);
      if (s.themePreference != null)
        localStorage.setItem("theme", s.themePreference);
      // Notifications
      if (s.emailSecurityAlerts != null)
        localStorage.setItem(
          "emailSecurityAlerts",
          s.emailSecurityAlerts.toString(),
        );
      if (s.emailTrainingReminders != null)
        localStorage.setItem(
          "emailTrainingReminders",
          s.emailTrainingReminders.toString(),
        );
      if (s.emailWeeklyDigest != null)
        localStorage.setItem(
          "emailWeeklyDigest",
          s.emailWeeklyDigest.toString(),
        );
      if (s.emailMarketing != null)
        localStorage.setItem("emailMarketing", s.emailMarketing.toString());
      if (s.inappAlertLevel != null)
        localStorage.setItem("alertLevel", s.inappAlertLevel);
      if (s.pushNotificationsEnabled != null)
        localStorage.setItem(
          "pushNotificationsEnabled",
          s.pushNotificationsEnabled.toString(),
        );
      // Security
      if (s.loginNotificationsEnabled != null)
        localStorage.setItem(
          "loginNotificationsEnabled",
          s.loginNotificationsEnabled.toString(),
        );
      // Localization
      if (s.language != null) {
        localStorage.setItem("language", s.language);
        // Notify LanguageContext in the same tab (storage event only fires cross-tab)
        window.dispatchEvent(
          new StorageEvent("storage", {
            key: "language",
            newValue: s.language,
          }),
        );
      }
      if (s.timezone != null) localStorage.setItem("timezone", s.timezone);
      if (s.dateFormat != null)
        localStorage.setItem("dateFormat", s.dateFormat);
      if (s.timeFormat != null)
        localStorage.setItem("timeFormat", s.timeFormat);
      // Privacy
      if (s.analyticsEnabled != null)
        localStorage.setItem("analyticsEnabled", s.analyticsEnabled.toString());
      if (s.profileVisibility != null)
        localStorage.setItem("profileVisibility", s.profileVisibility);
    }

    // Check verification status and handle conditional flows
    const isVerified = data.isVerified;
    const emailVerified = data.emailVerified;
    const phoneVerified = data.phoneVerified;

    console.log(
      "LoginFormModal: Verification status check - isVerified:",
      isVerified,
      "emailVerified:",
      emailVerified,
      "phoneVerified:",
      phoneVerified,
    );

    if (!isVerified && !emailVerified) {
      // Scenario 3: Email not verified - ask user to check email
      setErrorMessage(
        "Please check your email for verification instructions before logging in.",
      );
      return;
    }

    // Use UserContext's loginUser function to handle the logged-in user
    if (data.uuid && data.firstName && data.lastName) {
      console.log("LoginFormModal: Calling UserContext loginUser with:", data);

      // Set the user uuid cookie
      // document.cookie = `userUuid=${data.uuid}; path=/; max-age=86400`; // 1 day expiration

      // Create user object for UserContext
      // SECURITY: groupId is no longer sent from backend, use groupName instead
      const userForContext = {
        uuid: data.uuid,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || formData.email,
        groupName: data.groupName, // SECURITY: Use groupName instead of groupId
        // Include verification status for potential phone verification popup
        isVerified: isVerified,
        emailVerified: emailVerified,
        phoneVerified: phoneVerified,
        phone: data.phone,
        // Include any other user properties from the response
        ...data,
      };

      // Call UserContext's loginUser function
      loginUser(userForContext);

      // Automatically select the logged-in user as the selected user to start with.
      // Later if a different user is selected (if the logged in user is a group_admin or account_admin)
      // then that member user can be set to selectedUser.
      console.log(
        "LoginFormModal: Auto-selecting logged-in user as selected user:",
        userForContext,
      );
      selectUser(userForContext);

      // Note: Browser fingerprint collection is now handled in parallel with authentication
      // and is submitted automatically in the background

      // Check if phone verification is needed after successful login
      if (isVerified) {
        // Scenario 1: Fully verified - proceed normally
        setErrorMessage("");
        // Set initial page based on user's landing page preference, but
        // override to Dashboard (with Account Setup Progress expanded) when
        // the user does not have an active subscription.
        if (setHeroContent) {
          const preferredLandingPage = await resolveLandingPageAfterLogin();
          setHeroContent(preferredLandingPage);
        }
        onClose(); // Close the modal on successful login
      } else if (emailVerified && !phoneVerified) {
        // Scenario 2: Email verified but phone not verified - show phone verification popup
        toast(
          `Welcome ${data.firstName} ${data.lastName}! Please verify your phone number to complete your account setup.`,
          { duration: 6000, icon: "ℹ️" },
        );
        setErrorMessage("");
        // Set initial page based on user's landing page preference, with the
        // same no-subscription override as the fully-verified scenario above.
        if (setHeroContent) {
          const preferredLandingPage = await resolveLandingPageAfterLogin();
          setHeroContent(preferredLandingPage);
        }

        // Proactively send the phone verification SMS during login itself.
        // This ensures the user receives the text even if the popup has a
        // rendering delay or the user dismisses it before its useEffect fires.
        if (data.uuid) {
          console.log(
            "LoginFormModal: Proactively sending phone verification SMS for user:",
            data.uuid,
          );
          // Set optimistically BEFORE the async call completes so that
          // PhoneVerificationPopup mounts with smsSentByLogin=true and
          // does NOT fire a duplicate SMS from its own useEffect.
          if (setSmsSuccessfullySent) {
            setSmsSuccessfullySent(true);
          }
          UserAPI.sendPhoneVerification(data.uuid)
            .then(() => {
              console.log(
                "LoginFormModal: Phone verification SMS sent successfully",
              );
            })
            .catch((smsError) => {
              console.error(
                "LoginFormModal: Failed to send phone verification SMS:",
                smsError,
              );
              // SMS failed — tell the popup so it can retry via its useEffect
              if (setSmsSuccessfullySent) {
                setSmsSuccessfullySent(false);
              }
            });
        }

        onClose(); // Close the login modal

        // Show phone verification popup
        if (setIsPhoneVerificationPopupOpen) {
          console.log(
            "LoginFormModal: Triggering phone verification popup for user:",
            data.uuid,
          );
          setIsPhoneVerificationPopupOpen(true);
        } else {
          console.warn(
            "LoginFormModal: setIsPhoneVerificationPopupOpen not provided",
          );
        }
      }
    } else {
      console.warn(
        "LoginFormModal: Missing required user data in login response:",
        data,
      );
    }
  };

  const handleBackdropMouseDown = (e) => {
    // Only set flag if clicking directly on the backdrop (not on modal content)
    if (e.target.classList.contains("login-modal")) {
      setIsMouseDownOnBackdrop(true);
    }
  };

  const handleBackdropMouseUp = (e) => {
    // Only close if mouse was pressed down on backdrop AND released on backdrop
    if (e.target.classList.contains("login-modal") && isMouseDownOnBackdrop) {
      onClose(); // Close the modal when complete click (down + up) happens on backdrop
    }
    // Reset the flag regardless
    setIsMouseDownOnBackdrop(false);
  };

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("login-modal")) {
      onClose(); // Close the modal when clicking outside
    }
  };

  return (
    <>
      <div
        className="login-modal"
        onMouseDown={handleBackdropMouseDown}
        onMouseUp={handleBackdropMouseUp}
      >
        {/* Login Form View */}
        {currentView === "login" && (
          <div className="login-modal-content" data-theme={effectiveTheme}>
            <button
              className="login-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2>Welcome to KinShield</h2>
            <p>Log in or create your account</p>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <span
                    className="eye-icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </span>
                </div>{" "}
              </div>
              <div className="forgot-password-link">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    onClose();
                    onForgotPassword();
                  }}
                >
                  <strong>Forgot password?</strong>
                </button>
              </div>
              <p
                className={`error-message ${errorMessage ? "visible" : "hidden"}`}
              >
                {errorMessage}
              </p>
              <button type="submit" className="login-button">
                Sign In
              </button>
            </form>
            <div className="signup-divider">
              <span>or</span>
            </div>
            <div className="footer-links">
              New here?&nbsp;&nbsp;&nbsp;
              <button
                type="button"
                className="link-button signup-link"
                onClick={() => {
                  onCreateAccount();
                }}
              >
                <strong>Create an account</strong>
              </button>
            </div>
            <div className="login-footer-agreement">
              By logging in, you agree to our{" "}
              <button
                type="button"
                className="link-button-inline"
                onClick={() => setCurrentView("terms")}
              >
                Terms of Service
              </button>{" "}
              and{" "}
              <button
                type="button"
                className="link-button-inline"
                onClick={() => setCurrentView("privacy")}
              >
                Privacy Policy
              </button>
            </div>
          </div>
        )}

        {/* Terms of Service View */}
        {currentView === "terms" && (
          <div className="login-modal-content legal-view">
            <button
              className="back-to-login-button"
              onClick={() => setCurrentView("login")}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Login
            </button>
            <div className="legal-content-wrapper">
              <TermsAndConditions />
            </div>
          </div>
        )}

        {/* Privacy Policy View */}
        {currentView === "privacy" && (
          <div className="login-modal-content legal-view">
            <button
              className="back-to-login-button"
              onClick={() => setCurrentView("login")}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Login
            </button>
            <div className="legal-content-wrapper">
              <PrivacyPolicy />
            </div>
          </div>
        )}
      </div>

      {showMfaModal && mfaSessionId && (
        <MfaVerificationModal
          sessionId={mfaSessionId}
          mfaMethod={tempLoginData?.mfaMethod}
          onVerified={handleMfaVerified}
          onCancel={handleMfaCancel}
          onResendCode={handleResendMfaCode}
        />
      )}
    </>
  );
}

export default LoginFormModal;
